import type { KnowledgeBaseRepository } from "../../domain/repositories/knowledge-base-repository";
import type { CreateKnowledgeBaseInput } from "../../domain/entities/knowledge-base";
import type {
  KnowledgeBaseListModel,
  KnowledgeBaseModel,
  MetadataModel,
} from "../models/knowledge-base-model";
import {
  toKnowledgeBase,
  toKnowledgeBaseList,
  toEmbeddingModels,
} from "../mappers/knowledge-base-mapper";
import {
  parseResponse,
  parseSuccessResponse,
} from "@/core/data/parse-response";
import { fetchWithAuth } from "@/core/data/fetch-with-auth";

const toApiChunkingConfig = (
  input: CreateKnowledgeBaseInput["chunkingConfig"],
) => ({
  chunk_size: input.chunkSize,
  chunk_overlap: input.chunkOverlap,
  embedding_model: input.embeddingModelCode,
});

const toApiCreateBody = (input: CreateKnowledgeBaseInput) => ({
  name: input.name,
  chunking_config: toApiChunkingConfig(input.chunkingConfig),
});

export const httpKnowledgeBaseRepository: KnowledgeBaseRepository = {
  list: async ({ page, limit, name }) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ...(name ? { name } : {}),
    });
    const res = await fetchWithAuth(`/api/v1/knowledge-bases?${params}`);
    const data = await parseResponse<KnowledgeBaseListModel>(res);
    return toKnowledgeBaseList(data);
  },

  getById: async (id) => {
    const res = await fetchWithAuth(`/api/v1/knowledge-bases/${id}`);
    const data = await parseResponse<KnowledgeBaseModel>(res);
    return toKnowledgeBase(data);
  },

  create: async (input) => {
    const res = await fetchWithAuth("/api/v1/knowledge-bases", {
      method: "POST",
      body: JSON.stringify(toApiCreateBody(input)),
    });
    const data = await parseResponse<KnowledgeBaseModel>(res);
    return toKnowledgeBase(data);
  },

  update: async (id, input) => {
    const res = await fetchWithAuth(`/api/v1/knowledge-bases/${id}`, {
      method: "PUT",
      body: JSON.stringify(toApiCreateBody(input)),
    });
    const data = await parseResponse<KnowledgeBaseModel>(res);
    return toKnowledgeBase(data);
  },

  delete: async (id) => {
    const res = await fetchWithAuth(`/api/v1/knowledge-bases/${id}`, {
      method: "DELETE",
    });
    await parseSuccessResponse(res);
  },

  getEmbeddingModels: async () => {
    const res = await fetchWithAuth("/api/v1/metadata");
    const data = await parseResponse<MetadataModel>(res);
    return toEmbeddingModels(data);
  },
};
